import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Alert, Table, Tag, Progress } from 'antd';
import { 
  ShopOutlined, 
  TeamOutlined, 
  BookOutlined, 
  DollarOutlined,
  UserOutlined,
  RiseOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { dashboardService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import locale from 'antd/es/date-picker/locale/pt_BR';

dayjs.locale('pt-br');

const DashboardUnidades = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

  const fetchData = async (date) => {
    setLoading(true);
    try {
      const mes = date.month() + 1;
      const ano = date.year();
      const response = await dashboardService.getUnidades({ mes, ano });
      setData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard de unidades:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedDate);
    const interval = setInterval(() => fetchData(selectedDate), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatPercent = (value) => `${value}%`;

  const columnasRanking = [
    {
      title: 'Posição',
      key: 'posicao',
      width: 80,
      render: (_, __, index) => (
        <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#cd7f32' : 'default'}>
          {index + 1}º
        </Tag>
      ),
    },
    {
      title: 'Unidade',
      dataIndex: 'nome',
      key: 'nome',
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      ellipsis: true,
    },
    {
      title: 'Alunos',
      dataIndex: 'alunos',
      key: 'alunos',
      sorter: (a, b) => a.alunos - b.alunos,
      render: (val) => (
        <span>
          <TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {val}
        </span>
      ),
    },
    {
      title: 'Turmas',
      dataIndex: 'turmas',
      key: 'turmas',
      sorter: (a, b) => a.turmas - b.turmas,
      render: (val) => (
        <span>
          <BookOutlined style={{ marginRight: 8, color: '#52c41a' }} />
          {val}
        </span>
      ),
    },
    {
      title: 'Receita Total',
      dataIndex: 'receita_total',
      key: 'receita_total',
      sorter: (a, b) => a.receita_total - b.receita_total,
      render: (val) => <strong style={{ color: '#52c41a' }}>{formatCurrency(val)}</strong>,
    },
    {
      title: 'Receita Realizada',
      dataIndex: 'receita_realizada',
      key: 'receita_realizada',
      sorter: (a, b) => a.receita_realizada - b.receita_realizada,
      render: (val) => formatCurrency(val),
    },
    {
      title: 'Receita Pendente',
      dataIndex: 'receita_pendente',
      key: 'receita_pendente',
      sorter: (a, b) => a.receita_pendente - b.receita_pendente,
      render: (val) => <span style={{ color: val > 0 ? '#faad14' : 'inherit' }}>{formatCurrency(val)}</span>,
    },
    {
      title: 'Ticket Médio',
      dataIndex: 'ticket_medio',
      key: 'ticket_medio',
      sorter: (a, b) => a.ticket_medio - b.ticket_medio,
      render: (val) => formatCurrency(val),
    },
    {
      title: 'Alunos/Turma',
      dataIndex: 'media_alunos_turma',
      key: 'media_alunos_turma',
      sorter: (a, b) => a.media_alunos_turma - b.media_alunos_turma,
      render: (val) => val.toFixed(1),
    },
  ];

  if (!data) {
    return (
      <div className="dashboard-container">
        <Card loading={loading}>
          <p>Carregando dados...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="dashboard-page dashboard-container">
      <div className="dashboard-header">
        <div>
          <h2>📊 Dashboard de Unidades/Escolas</h2>
          <p className="dashboard-subtitle">
            Análise Comparativa de Performance - {data.periodo.mes_nome}/{data.periodo.ano}
          </p>
        </div>
        <DatePicker
          picker="month"
          value={selectedDate}
          onChange={handleDateChange}
          format="MMMM/YYYY"
          locale={locale}
          allowClear={false}
        />
      </div>

      {/* KPIs Gerais */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Unidades Ativas"
              value={data.kpis.total_unidades}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Total de Alunos"
              value={data.kpis.total_alunos}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Total de Turmas"
              value={data.kpis.total_turmas}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Receita Total"
              value={data.kpis.receita_total}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14' }}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Média Alunos/Unidade"
              value={data.kpis.media_alunos_unidade}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#13c2c2' }}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Média Receita/Unidade"
              value={data.kpis.media_receita_unidade}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#eb2f96' }}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
      </Row>

      {/* Top 3 Unidades */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="🏆 Top 5 Unidades por Receita" bordered={false}>
            {data.top_receita && data.top_receita.length > 0 ? (
              data.top_receita.map((unidade, index) => (
                <div key={unidade.id} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>
                      <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#cd7f32' : 'blue'}>
                        {index + 1}º
                      </Tag>
                      <strong>{unidade.nome}</strong>
                    </span>
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                      {formatCurrency(unidade.receita_total)}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {unidade.alunos} alunos • {unidade.turmas} turmas • Ticket: {formatCurrency(unidade.ticket_medio)}
                  </div>
                  <Progress 
                    percent={Math.round((unidade.receita_total / data.top_receita[0].receita_total) * 100)} 
                    strokeColor="#52c41a"
                    showInfo={false}
                    size="small"
                  />
                </div>
              ))
            ) : (
              <Alert message="Nenhum dado disponível" type="info" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="👥 Top 5 Unidades por Alunos" bordered={false}>
            {data.top_alunos && data.top_alunos.length > 0 ? (
              data.top_alunos.map((unidade, index) => (
                <div key={unidade.id} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>
                      <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#cd7f32' : 'blue'}>
                        {index + 1}º
                      </Tag>
                      <strong>{unidade.nome}</strong>
                    </span>
                    <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                      {unidade.alunos} alunos
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {unidade.turmas} turmas • Média: {unidade.media_alunos_turma.toFixed(1)} alunos/turma
                  </div>
                  <Progress 
                    percent={Math.round((unidade.alunos / data.top_alunos[0].alunos) * 100)} 
                    strokeColor="#1890ff"
                    showInfo={false}
                    size="small"
                  />
                </div>
              ))
            ) : (
              <Alert message="Nenhum dado disponível" type="info" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Gráficos de Distribuição */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="📊 Distribuição de Alunos (Top 10)" bordered={false}>
            {data.distribuicao_alunos && data.distribuicao_alunos.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.distribuicao_alunos} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="unidade" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="alunos" fill="#1890ff" name="Alunos" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert message="Nenhum dado disponível" type="info" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="💰 Distribuição de Receita (Top 10)" bordered={false}>
            {data.distribuicao_receita && data.distribuicao_receita.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.distribuicao_receita} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="unidade" type="category" width={120} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="receita" fill="#52c41a" name="Receita" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert message="Nenhum dado disponível" type="info" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Turmas por Modalidade e Inadimplência */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="📚 Turmas por Modalidade (Top 8 Unidades)" bordered={false}>
            {data.turmas_por_modalidade && data.turmas_por_modalidade.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.turmas_por_modalidade}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="unidade" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {Object.keys(data.turmas_por_modalidade[0] || {})
                    .filter(key => key !== 'unidade')
                    .map((modalidade, index) => (
                      <Bar 
                        key={modalidade} 
                        dataKey={modalidade} 
                        stackId="a" 
                        fill={COLORS[index % COLORS.length]} 
                      />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert message="Nenhum dado disponível" type="info" />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                Taxa de Inadimplência por Unidade (Top 10)
              </span>
            } 
            bordered={false}
          >
            {data.inadimplencia_por_unidade && data.inadimplencia_por_unidade.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.inadimplencia_por_unidade} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="%" />
                  <YAxis dataKey="unidade" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="taxa" fill="#faad14" name="Taxa (%)" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert message="Nenhum dado disponível" type="info" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Ranking Completo */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="📋 Ranking Completo de Unidades" bordered={false}>
            <Table
              dataSource={data.ranking_unidades}
              columns={columnasRanking}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
              scroll={{ x: 1200 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardUnidades;
